import React, { useState, useEffect } from 'react';
import { getFirestore, doc, updateDoc, onSnapshot, getDocs, getDoc, collection } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';

const ChartsTab = () => {
  const [selectedDate, setSelectedDate] = useState(getMondayKey(new Date()));
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState({});
  const db = getFirestore();
  const functions = getFunctions();
  const [isCommitted, setIsCommitted] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    console.log(`[DEBUG] Starting logic for: ${selectedDate}`);
    setDraft(null); // Clear old data immediately to avoid flickering
    setIsCommitted(false);

    const weeklyRef = doc(db, 'weeklyCharts', selectedDate);
    let unsubDraft = null;

    // 1. Check for Committed Chart
    const unsubWeekly = onSnapshot(weeklyRef, async (docSnap) => {
      if (!isMounted) return;

      console.log(`[DEBUG] weeklyCharts/${selectedDate} exists:`, docSnap.exists());

      if (docSnap.exists()) {
        console.log("[DEBUG] Found Committed Chart. Fetching positions...");
        try {
          // Kill draft listener if we find a real chart
          if (unsubDraft) {
            unsubDraft();
            unsubDraft = null;
          }

          const posSnap = await getDocs(collection(db, 'weeklyCharts', selectedDate, 'positions'));
          const sortedPositions = posSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.position || 0) - (b.position || 0));

          if (isMounted) {
            setDraft({ chart: sortedPositions });
            setIsCommitted(true);
            setLoading(false);
            console.log("[DEBUG] UI State updated with Committed Chart.");
          }
        } catch (err) {
          console.error("Failed to fetch positions:", err);
          if (isMounted) setLoading(false);
        }
      } else {
        // 2. Fallback to Draft
        const draftRef = doc(db, 'chartDrafts', selectedDate);
        unsubDraft = onSnapshot(draftRef, (s) => {
          if (!isMounted) return;
          
          if (s.exists()) {
            setDraft({ id: s.id, ...s.data() });
          } else {
            setDraft(null);
          }
          setIsCommitted(false);
          setLoading(false);
        });
      }
    });

    return () => {
      isMounted = false;
      unsubWeekly();
      if (unsubDraft) unsubDraft();
    };
  }, [db, selectedDate]);

  const stageCatalogueToggle = (songId, currentStatus) => {
    setPendingUpdates(prev => {
      const next = { ...prev };
      
      // If we toggle back to the original status, remove it from pending
      if (next[songId] === !currentStatus) {
        delete next[songId];
      } else {
        next[songId] = !currentStatus;
      }
      
      return next;
    });
  };

  const handleDownload = async (e, song) => {
    e.stopPropagation();
    setDownloadingId(song.id);
    const downloadToast = toast.loading("Locating audio file...");

    try {
      let audioUrl = song.audioURL;

      // IF the chart document is missing the URL, go find it in the main 'songs' collection
      if (!audioUrl) {
        const songDoc = await getDoc(doc(db, 'songs', song.id));
        if (songDoc.exists()) {
          audioUrl = songDoc.data().audioURL;
        }
      }

      if (!audioUrl) {
        toast.dismiss(downloadToast);
        return toast.error("Audio URL not found in library.");
      }

      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileExtension = audioUrl.split('.').pop().split('?')[0] || 'mp3';
      
      link.href = url;
      link.download = `${song.artistName} - ${song.title}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.dismiss(downloadToast);
      toast.success("Ready.");
    } catch (error) {
      toast.dismiss(downloadToast);
      toast.error("Download failed.");
      console.error(error);
    } finally {
      setDownloadingId(null);
    }
  };

  const runBulkCatalogueUpdate = async () => {
    const updateArray = Object.entries(pendingUpdates).map(([id, isBackCatalogue]) => ({
      id,
      isBackCatalogue
    }));

    if (updateArray.length === 0) return;
    setLoading(true);

    try {
      const updateTags = httpsCallable(functions, 'updateBackCatalogueTags');
      await updateTags({ updates: updateArray }); // Send batch to v2 function
      alert(`Updated ${updateArray.length} songs successfully.`);
      setPendingUpdates({}); 
    } catch (error) {
      alert("Bulk Update Failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshDraft = async () => {
      console.log("--- Refresh Draft Triggered ---");
      console.log("Selected Date:", selectedDate);
      
      setLoading(true);
      try {
          const generateDraft = httpsCallable(functions, 'generateChartDraft');
          
          console.log("Calling Cloud Function 'generateChartDraft'...");
          const result = await generateDraft({ targetDate: selectedDate });
          
          console.log("Cloud Function Response Success:", result.data);
          alert("Draft Refreshed Successfully");
      } catch (error) {
          // This will tell us if it's a CORS issue, a 404, or a Permission error
          console.error("FULL ERROR OBJECT:", error);
          console.error("Error Code:", error.code);
          console.error("Error Message:", error.message);
          console.error("Error Details:", error.details);
          
          alert(`Error refreshing draft: ${error.message} (Code: ${error.code})`);
      } finally {
          setLoading(false);
          console.log("--- Refresh Draft Transaction Complete ---");
      }
  };

  const commitChart = async () => {
    if (!window.confirm(`Commit the chart for ${selectedDate}?`)) return;
    setLoading(true);
    try {
      const commitChartFn = httpsCallable(functions, "commitChart", {
        timeout: 300000 // 5 minutes in milliseconds
      });

      // Call commitChartFn instead of 'commit'
      const response = await commitChartFn({ targetWeek: selectedDate });

      console.log("Chart commit response:", response.data);
      alert("Chart committed successfully!");
    } catch (error) {
      console.error("Error committing chart:", error);
      alert("Error committing chart: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const sortedChart = draft?.chart ? [...draft.chart].sort((a, b) => {
    // Try position first (committed), then calculatedPos (draft)
    const posA = a.position !== undefined ? a.position : (a.calculatedPos !== undefined ? a.calculatedPos : 999);
    const posB = b.position !== undefined ? b.position : (b.calculatedPos !== undefined ? b.calculatedPos : 999);
    return posA - posB;
  }) : [];

  return (
    <div className="flex flex-col h-full">
      {/* Header with Controls - ALWAYS VISIBLE */}
      <div className="p-6 bg-slate-50 border-b flex justify-between items-center">
        <div className="flex items-center gap-4">
          <input 
            type="date" 
            value={selectedDate} 
            onChange={(e) => setSelectedDate(getMondayKey(new Date(e.target.value)))}
            className="p-2 border rounded-lg font-bold text-slate-700"
          />
          <p className="text-sm text-slate-500">Targeting Week: {selectedDate}</p>
        </div>
        <div className="flex gap-3">
          {isCommitted ? (
            // Wrap both the button and the badge in a Fragment
            <>
              <button 
                onClick={async () => {
                  const summarySnap = await getDoc(doc(db, 'weeklyCharts', selectedDate, 'summaries', 'chartSummary'));
                  if (summarySnap.exists()) {
                    // This copies the version with \n (newlines) for Excel
                    navigator.clipboard.writeText(summarySnap.data().chartAsText);
                    alert("Summary copied for Excel!");
                  } else {
                    alert("Summary document not found.");
                  }
                }}
                className="px-4 py-2 bg-slate-800 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-black transition-all shadow-md"
              >
                Copy Excel Summary
              </button>

              <div className="flex items-center gap-2 px-6 py-2 bg-emerald-100 text-emerald-700 font-black uppercase text-xs tracking-widest rounded-xl border border-emerald-200">
                <span className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse" />
                Official Chart Published
              </div>
            </>
          ) : (
            // YOU NEED THIS FRAGMENT WRAPPER HERE
            <>
              {Object.keys(pendingUpdates).length > 0 && (
                <button 
                  onClick={runBulkCatalogueUpdate}
                  className="px-4 py-2 bg-amber-500 text-white text-xs font-black uppercase tracking-widest rounded-lg hover:bg-amber-600 shadow-md transition-all"
                >
                  Save {Object.keys(pendingUpdates).length} Changes
                </button>
              )}
              
              <button 
                onClick={refreshDraft}
                disabled={loading}
                className="px-6 py-2 bg-white border border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-100 disabled:opacity-50 transition-all"
              >
                {loading ? 'Processing...' : 'Refresh Logic'}
              </button>

              <button 
                onClick={commitChart}
                disabled={loading || !draft}
                className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-200 disabled:opacity-50 transition-all"
              >
                Commit to Platform
              </button>
            </>
          )}
        </div>
      </div>

      {/* Conditional Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-tighter border-b">
                <th className="px-6 py-4 w-16">Pos</th>
                <th className="px-2 py-4">Artist & Song Info</th>
                <th className="px-6 py-4 w-24 text-center">Trend</th>
                <th className="px-6 py-4 w-24 text-center">Download</th>
                <th className="px-6 py-4 w-32 text-center">Catalogue</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedChart.map((song, index) => {
                // 1. Identify if it's in the swamp based on the data field
                const isSwamp = (song.pos || 0) > 50;
                const lastPos = song.lastRank || song.lastPosition;
                const isNewEntry = !lastPos; // <--- The variable the error is complaining about
                const diff = lastPos ? lastPos - (song.pos || 0) : 0;
                const displayRank = song.pos || (index + 1);
                
                const isBackCatalogue = pendingUpdates[song.id] !== undefined 
                  ? pendingUpdates[song.id] 
                  : song.isBackCatalogue;

                return (
                  <tr key={song.id} className={`transition-colors ${isSwamp ? 'bg-orange-50/30' : 'hover:bg-blue-50/20'}`}>
                    
                    {/* Updated Pos Column: Uses displayRank and conditional colors */}
                    <td className={`px-6 py-4 font-black text-lg italic ${isSwamp ? 'text-orange-500' : 'text-blue-600'}`}>
                      #{displayRank}
                    </td>

                    <td className="px-2 py-4 text-left">
                      <div className="font-bold text-slate-900 leading-tight">
                        {song.artistName}
                      </div>
                      <div className="text-sm text-slate-500">
                        {song.title}
                      </div>
                      
                      
                    </td>
                      
                      {/* Optional: Add a small indicator if the position changed 
                      {song.pos && Number(song.pos) !== currentRank && (
                        <div className="text-[10px] font-bold text-amber-600 uppercase mt-1">
                          {currentRank < song.pos ? '▲' : '▼'} Moved from #{song.pos}
                        </div>
                      )}
                    </td>*/}

                    {/* NEW TREND COLUMN */}
                    <td className="px-6 py-4 text-center">
                      <div className="flex flex-col items-center justify-center">
                        {isNewEntry ? (
                          <span className="text-[10px] font-black px-2 py-0.5 bg-blue-100 text-blue-600 rounded uppercase tracking-widest">
                            New
                          </span>
                        ) : diff > 0 ? (
                          <div className="flex flex-col items-center text-emerald-500">
                            <span className="text-lg leading-none">▲</span>
                            <span className="text-[10px] font-black">{diff}</span>
                          </div>
                        ) : diff < 0 ? (
                          <div className="flex flex-col items-center text-rose-500">
                            <span className="text-lg leading-none">▼</span>
                            <span className="text-[10px] font-black">{Math.abs(diff)}</span>
                          </div>
                        ) : (
                          <span className="text-slate-300 text-xs font-bold">—</span>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={(e) => handleDownload(e, song)}
                        disabled={downloadingId === song.id}
                        className={`text-[10px] font-bold px-3 py-1.5 rounded-md transition-all uppercase tracking-tighter ${
                          downloadingId === song.id 
                            ? "bg-gray-200 text-gray-400" 
                            : "bg-slate-100 text-slate-600 hover:bg-slate-800 hover:text-white"
                        }`}
                      >
                        {downloadingId === song.id ? "Wait" : "DOWNLOAD"}
                      </button>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        checked={isBackCatalogue || false}
                        onChange={() => stageCatalogueToggle(song.id, song.isBackCatalogue)}
                        className={`h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer transition-all ${
                          pendingUpdates[song.id] !== undefined ? 'ring-2 ring-amber-400' : ''
                        }`}
                      />
                    </td>
                  </tr>
                ); // No extra '>' here!
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ChartsTab;

function getMondayKey(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    return monday.toISOString().split('T')[0];
}